import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import LogoNavigation from '../components/commons/organisms/LogoNavigation';
import { PALE, SOFT_TOMATO } from '../components/colors';
import OrderSummary from '../components/checkout/organisms/OrderSummary';
import { useNavigate, useParams } from 'react-router-dom';
import APIService from '../utils/api';
import Skeleton from 'react-loading-skeleton';
import SearchBar from '../components/shop/organisms/SearchBar';
import OrderStepper from '../components/order/organisms/OrderStepper';
import { throttle } from 'lodash';
import FormTitle from '../components/checkout/molecules/FormTitle';
import TrackOrderForm from '../components/order/organisms/TrackOrderForm';
import TogglePanel from '../components/commons/molecules/TogglePanel';
import usePageView from '../hooks/use-page-view';
import { MIXPANEL_PAGE, MIXPANEL_ACTION } from '../utils/constants';
import MixpanelService from '../utils/mixpanel';

interface TrackOrderProps {};

const TrackOrder = ({}: TrackOrderProps) => {

  const navigate = useNavigate();

  const steps = [
    { label: 'Processing' },
    { label: 'Payment-Received' },
    { label: 'For-Delivery' },
    { label: 'Delivered' },
  ];

  const {
    order: orderNumber
  } = useParams();

  const [order, setOrder] = useState<any>();
  const [consumerDetails, setConsumerDetails] = useState<any>();

  usePageView(MIXPANEL_PAGE.TRACK_ORDER)

  const orderStatus = useMemo(() => {
    if (!order) {
      return -1;
    }
    switch (order.status) {
      case 'Processing':
        return 0;
      case 'Payment-Received': 
        return 1;
      case 'For-Delivery':
        return 3;
      case 'Completed:':
        return 4;
      default: 
        return -1;
    }
  }, [order])

  const fetchOrder = async (orderNumber: string) => {
    if (!orderNumber) return;
    // 1751526211000
    try {
      const data = await APIService.getOrder(orderNumber);
      setConsumerDetails(data.customer || data.guest)
      setOrder(data);
    } catch (e) {}
  }

  useEffect(() => {
    if (orderNumber) {
      fetchOrder(orderNumber);
    }
  }, [orderNumber])

  const throttledRequest = throttle((orderNumber: string) => {
    // fetchOrder(orderNumber)
    navigate(`/track/${orderNumber}`)
  }, 2000); // onl


  return (
    <>
      <LogoNavigation />
      <Layout>
        { !order ? (
          <OrderSummaryInfo>
            <HeadingTitle>
              <p>
                TRACK ORDERS
              </p>
            </HeadingTitle>
            <HeadingSubtitle>
              <p>
                {`Please enter your order number to view your order details or track your delivery.`.toUpperCase()}
              </p>
            </HeadingSubtitle>
            <TrackOrderForm
              onTrackOrder={(email, orderNumber) => {
                MixpanelService.track(`
                  ${MIXPANEL_PAGE.TRACK_ORDER} - ${MIXPANEL_ACTION.BUTTON_CLICK}`, 
                  {
                    item: 'Sumbit',
                    orderNumber: orderNumber
                });
                throttledRequest(orderNumber);
              }}
            /> 
          </OrderSummaryInfo>
        ) : (
          <OrderSummaryInfo>
            <HeadingTitle>
              <p>
                TRACK ORDERS
              </p>
            </HeadingTitle>
            <Heading>
              <HeadingDetails>
                <p>
                  <strong>{`HI, ${consumerDetails.firstName}`.toUpperCase()}</strong>
                </p>
                <OrderNumber>
                  {`Order #: ${order.PONumber}`.toUpperCase()}
                </OrderNumber>
                <OrderDate>
                  <p>{moment(order.createdDate).format('MM-DD-YYYY')}</p>
                </OrderDate>
              </HeadingDetails>
            </Heading>

            <Content>
              <ContentLabel>
                <p>CONTACT INFORMATION</p>
              </ContentLabel>
              <ContentValue>
                <p>{consumerDetails.email.toUpperCase()}</p>
              </ContentValue>
              <ContentValue>
                <p>{consumerDetails.mobile.toUpperCase()}</p>
              </ContentValue>

              <ContentLabel>
                <p>DELIVERY</p>
              </ContentLabel>
              <ContentValue>
                <p>{order.address?.country?.toUpperCase() || ''}</p>
                <p>{order.address.street.toUpperCase()}</p>
                <p>{order.address.town.toUpperCase()}</p>
                <p>{order.address.province.toUpperCase()}</p>
                <p>{order.address.postCode.toUpperCase()}</p>
              </ContentValue>
            </Content>

            <br/>
            {orderStatus !== -1 && (
              <OrderStepper steps={steps} currentStep={orderStatus} />
            )}
            <br/>

            <OrderSummary
              products={order.items}
              totalAmount={order.totalPrice}
              shippingAmount={0}
            />
          </OrderSummaryInfo>
        )}
        
        <FAQ>
            <HeadingTitle>
              <p>
                FAQ...
              </p>
            </HeadingTitle>
            <TogglePanel 
              title={'Ordering & Shipping'}
              contents={<>
                <p><strong>How long does it take to process my order?</strong></p>
                <p>
                  Orders are typically processed within 1–2 business days after confirmation.
                  For bulk or special requests, we may reach out to coordinate dispatch.
                </p>

                <p><strong>When will I receive my order?</strong></p>
                <ul>
                  <li>Metro Manila: 1–3 business days</li>
                  <li>Luzon: 2–5 business days</li>
                  <li>Visayas & Mindanao: 5–7 business days</li>
                </ul>
                <p>Courier delays may occur during peak seasons.</p>

                <p><strong>Do you offer same-day delivery?</strong></p>
                <p>
                  Yes, same-day delivery is available within Metro Manila for select items. Kindly message
                  our team via Viber or direct line before 12 noon to confirm.
                </p>
              </>}
            />
            <hr />
            <TogglePanel 
              title={'Payment & Invoicing'}
              contents={<>
                <p><strong>What payment methods do you accept?</strong></p>
                <p>We accept bank transfer, Paypal, and credit card. Payment confirmation is required before shipping.</p>
              </>}
            />
             <hr />
            <TogglePanel 
              title={'Returns & Exchanges'}
              contents={
                <>
                  <ul>
                    <li>You have 7 working days from the date of receipt to return an item.</li>
	                  <li>Returns will only be accepted if the received item does not match your order.</li>
                  </ul>
                  <p>The item must be unopened and in its original condition</p>
                  <ul>
                    <li>Once the returned item is received and confirmed, we will ship the exchange item.</li>
                  <li>Health and Glow PH will cover the return shipping cost for qualified cases.</li>
                  </ul>
                </>
              }
            />
            <hr />
            <TogglePanel 
              title={'Damaged or Broken Items'}
              contents={
                <>
                  <ul>
                    <li>We take every precaution to ensure your order is accurate, properly packed, and well-protected.</li>
                    <li>Health and Glow PH is not responsible for items damaged during shipping.</li>
                    <li>Items not covered by shipping insurance are sent at the customer’s risk.</li>
                    <li>If you receive items with factory defects or damage, please send us photo or video documentation, and we will coordinate with the manufacturer to replace the goods.</li>
                  </ul>
                </>
              }
            />
            <hr />
            <TogglePanel 
              title={'Customer Advisory: Reporting Broken, Damaged, or Missing Items'}
              contents={
                <>
                <p><strong>To ensure a smooth resolution process:</strong></p>
                  <ul>
                    <li>Please record a video of you unboxing the parcel from start to finish.</li>
                    <li>This video will serve as proof in case of damage, missing items, or quality concerns.</li>
                    <li>Photos alone will not be accepted as valid proof, to ensure fairness to both parties.</li>
                  </ul>
                </>
              }
            />
            <hr />
            <TogglePanel 
              title={'Shipping Delays'}
              contents={
                <>
                  <ul>
                    <li>Provided shipping dates are estimates only.</li>
                    <li>Health and Glow PH is not responsible for delays caused by the courier, but we will assist in tracking and resolving issues.</li>
                  </ul>
                </>
              }
            />
          </FAQ>
      </Layout>
    </>
  )
};

export default TrackOrder;


const StyledSkeleton = styled(Skeleton)``;

const Layout = styled.div`
  display: flex;
  flex-direction: column;

  @media(min-width: 768px) {
    max-width: 1200px; /* or 1280px */
    margin: 0 auto;
    padding: 1rem 1rem; /* optional side padding */
    flex-direction: row;
  }
`;

const OrderSummaryInfo = styled.div`
  flex: 1;
  padding: 0rem 1rem;
  @media(min-width: 768px) {
    padding: 0rem;
  }
`;

const FAQ = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 1rem 1rem;

  @media(min-width: 768px) {
    padding-top: 0rem;
  }
`;

const Root = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 1rem 1rem;

  @media(min-width: 768px) {
    padding-top: 0rem;
  }
`;

const Heading = styled.div`
  display: flex;
  flex-direction: row;
`;

const HeadingTitle = styled.div`
  p {
    font-family: 'Poppins-Semibold';
    font-size: 1rem;
    margin: 0 0:
  }
`;

const HeadingSubtitle = styled.div`
  p {
    font-family: 'Poppins-Medium';
    font-size: 0.8rem;
    margin: 0 0:
  }
`;

const ImgCheckCircle = styled.div`
  height: 64px;

  img {
    width: 100%;
    height: inherit;
    object-fit: cover;
    fill: red;
  }
`;

const HeadingDetails = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  

  p {
    margin: 0 0;
    // font-family: 'Poppins-Light';
    // font-size: 0.8rem;
  }
`;

const OrderNumber = styled.p`
  font-family: 'Poppins-SemiBold';
  font-size: 1rem;
  margin: 0 0;
  color: ${SOFT_TOMATO};
`;

const OrderDate = styled.p`
  font-family: 'Poppins-Light';
  font-size: 0.8rem;
  margin: 0 0;
  color: ${SOFT_TOMATO};
`;

const Content = styled.div`
  padding: 1rem 0rem;
   button {
    margin: 1rem 0rem;
    width: 100%;
    border: none;
    background-color: ${SOFT_TOMATO};
    color: white;
    border-radius: 6px;
    height: 40px;
    font-family: 'Poppins-SemiBold';
    font-size: 0.8rem;
  };
`;

const ContentLabel = styled.div`
  p {
    margin: 0rem 0rem;
    font-family: 'Poppins-Medium';
    font-size: 1rem;
    margin-top: 0.5rem;
  }
`;

const ContentValue = styled.div`
  p {
    margin: 0 0;
    font-family: 'Poppins-Light';
    font-size: 0.8rem;
  }
`;